import type { ComponentChildren } from 'preact'
import { useEffect } from 'preact/hooks'
import { useDocumentEvent } from '../hooks/use-ui-listeners'
import { keyframes, styled } from '../setup'
import { borders, colors, components, spacing, typography } from '../theme'

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`

const slideUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`

const Backdrop = styled('div')`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${spacing['2xl']};
  z-index: 1000;
  animation: ${fadeIn} 0.2s ease-out;
`

const ModalCard = styled('div')<{ maxWidth?: string }>`
  background: ${colors.bgSurface};
  border: ${components.card.border};
  border-radius: ${borders.radiusLg};
  box-shadow: 0 4px 24px ${colors.shadowMedium};
  width: 100%;
  max-width: ${(props) => props.maxWidth || '480px'};
  max-height: 90vh;
  overflow-y: auto;
  animation: ${slideUp} 0.2s ease-out;
`

const ModalHeader = styled('div')<{ hasTitle: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${spacing['2xl']};
  border-bottom: ${(props) => (props.hasTitle ? `1px solid ${colors.border}` : 'none')};
`

const ModalTitle = styled('h2')`
  margin: 0;
  font-size: ${typography.fontSize2xl};
  font-weight: ${typography.fontWeightSemibold};
  color: ${colors.textPrimary};
`

const CloseButton = styled('button')`
  background: transparent;
  border: none;
  padding: ${spacing.sm};
  cursor: pointer;
  color: ${colors.textMuted};
  font-size: ${typography.fontSizeLg};
  line-height: 1;
  border-radius: ${borders.radius};
  transition: background 0.2s, color 0.2s;

  &:hover {
    background: ${colors.bgSurfaceAlt};
    color: ${colors.textPrimary};
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(22, 101, 52, 0.3);
  }
`

const ModalContent = styled('div')`
  padding: ${spacing['2xl']};
`

interface ModalProps {
  children: ComponentChildren
  title?: string
  isOpen: boolean
  onClose: () => void
  maxWidth?: string
  closeOnBackdrop?: boolean
}

export function Modal({
  children,
  title,
  isOpen,
  onClose,
  maxWidth,
  closeOnBackdrop = true,
}: ModalProps) {
  // Handle escape key
  useDocumentEvent(
    'keydown',
    (e) => {
      if (e.key === 'Escape') {
        onClose()
      }
    },
    { enabled: isOpen },
  )

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }

    return () => {
      document.body.style.overflow = ''
    }
  }, [isOpen])

  if (!isOpen) {
    return null
  }

  const handleBackdropClick = (e: MouseEvent) => {
    if (closeOnBackdrop && e.target === e.currentTarget) {
      onClose()
    }
  }

  return (
    <Backdrop onClick={handleBackdropClick}>
      <ModalCard
        maxWidth={maxWidth}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? 'modal-title' : undefined}
      >
        {(title || true) && (
          <ModalHeader hasTitle={!!title}>
            {title ? <ModalTitle id="modal-title">{title}</ModalTitle> : <span />}
            <CloseButton onClick={onClose} aria-label="Close modal">
              &times;
            </CloseButton>
          </ModalHeader>
        )}
        <ModalContent>{children}</ModalContent>
      </ModalCard>
    </Backdrop>
  )
}
